/**
 * Ultimo estado conhecido, no localStorage, com escopo de tenant.
 *
 * Responsabilidade: persistir o que a tela precisa para abrir SEM rede, e
 * dizer de quando esse dado e.
 *
 * NAO e responsabilidade deste arquivo: guardar credencial (nunca; ver o
 * cabecalho de `api.ts`), decidir o que a tela mostra (`store.ts`), nem
 * cachear bytes de imagem (isso e o service worker).
 *
 * ## Por que o localStorage e nao o cache do service worker
 *
 * Um service worker que respondesse `/v1/devices` do cache devolveria
 * `200 OK` com dado de ontem, e a tela nao teria como distinguir isso de uma
 * resposta fresca. O gate desta fase e justamente o contrario: abrir offline e
 * dizer, sem rodeios, que o que esta na tela e velho e de quando. Guardando
 * aqui, `gravadoEm` vem junto com o dado e a etiqueta e sempre verdadeira.
 *
 * ## Por que a chave carrega o tenant
 *
 * A RLS garante que a API nunca entrega dado de outro tenant. No navegador,
 * quem trocasse de conta veria o cache da conta anterior enquanto o primeiro
 * snapshot nao chegasse — isolamento quebrado no ultimo metro, depois de todo
 * o cuidado do servidor. A chave e por tenant e o `sair()` apaga tudo.
 */

import type {
  AlertaOut,
  AparelhoNoSnapshot,
  CommandOut,
  TelemetrySample,
  UploadOut,
} from "./tipos";

const PREFIXO = "gfcloud:v1";
const CHAVE_SESSAO = `${PREFIXO}:sessao`;

/** ~2 h a 60 s (contrato §4). Suficiente para a sparkline, curto para caber. */
export const MAX_AMOSTRAS = 120;
/** Um dia de captura horaria por aparelho. */
const MAX_UPLOADS = 24;
// A aba "Ao vivo" mostra uma foto so; guardar as tres ultimas cobre o "voltar
// uma" sem inchar a cota. A nuvem guarda dez (`uploads.MAX_SNAPSHOTS`), e
// baixar as outras sete de novo custa um GET.
const MAX_SNAPSHOTS = 3;
const MAX_COMANDOS = 20;
// O feed de alertas e curto por natureza (borda, nao amostra): 30 cobrem
// semanas de operacao normal e cabem folgados na cota. Existe no disco pelo
// mesmo motivo dos comandos — abrir em modo aviao e ver o que aconteceu ontem
// a noite e metade do valor de ter um feed.
const MAX_ALERTAS = 30;

/** Quem estava logado — sem token, so identidade, para saber que cache abrir. */
export interface SessaoLembrada {
  tenant_id: string;
  tenant_name: string;
  email: string;
  tier: string;
}

export interface Instantaneo {
  gravadoEm: number;
  aparelhos: AparelhoNoSnapshot[];
  selecionado: string | null;
  telemetria: Record<string, TelemetrySample[]>;
  uploads: Record<string, UploadOut[]>;
  /**
   * Fotos sob demanda (contrato §7). Opcional porque um instantaneo gravado
   * por uma versao anterior da PWA nao tem o campo — e o cache antigo tem que
   * continuar abrindo, senao a primeira visita depois de um deploy nasce sem
   * tela nenhuma.
   */
  snapshots?: Record<string, UploadOut[]>;
  comandos: Record<string, CommandOut[]>;
  /**
   * Feed de alertas (C12) e o contador de nao-lidos. Opcionais pelo mesmo
   * motivo de `snapshots`: um instantaneo gravado por uma versao anterior da
   * PWA nao os tem, e o cache antigo tem que continuar abrindo.
   */
  alertas?: AlertaOut[];
  naoLidos?: number;
}

function ler<T>(chave: string): T | null {
  try {
    const bruto = localStorage.getItem(chave);
    return bruto === null ? null : (JSON.parse(bruto) as T);
  } catch {
    // localStorage bloqueado (modo privado de alguns navegadores) ou JSON
    // corrompido. Nao ter cache e um modo de operacao valido: a PWA so perde
    // a abertura offline.
    return null;
  }
}

function gravar(chave: string, valor: unknown): void {
  try {
    localStorage.setItem(chave, JSON.stringify(valor));
  } catch {
    // Cota estourada ou storage negado. Silencio de proposito: falhar aqui
    // nao pode derrubar uma tela que esta funcionando com dado ao vivo.
  }
}

export const lerSessao = (): SessaoLembrada | null => ler<SessaoLembrada>(CHAVE_SESSAO);

export const gravarSessao = (sessao: SessaoLembrada): void =>
  gravar(CHAVE_SESSAO, sessao);

export const lerInstantaneo = (tenantId: string): Instantaneo | null =>
  ler<Instantaneo>(`${PREFIXO}:${tenantId}`);

export function gravarInstantaneo(
  tenantId: string,
  dados: Omit<Instantaneo, "gravadoEm">,
): void {
  gravar(`${PREFIXO}:${tenantId}`, {
    gravadoEm: Date.now(),
    aparelhos: dados.aparelhos,
    selecionado: dados.selecionado,
    telemetria: podarMapa(dados.telemetria, MAX_AMOSTRAS),
    uploads: podarMapa(dados.uploads, MAX_UPLOADS),
    snapshots: podarMapa(dados.snapshots ?? {}, MAX_SNAPSHOTS),
    comandos: podarMapa(dados.comandos, MAX_COMANDOS),
    // `slice(0, ...)` e nao `podarMapa`: o feed e uma lista so, do tenant, e
    // ja vem mais novo primeiro — a cauda a descartar e a do fim.
    alertas: (dados.alertas ?? []).slice(0, MAX_ALERTAS),
    naoLidos: dados.naoLidos ?? 0,
  } satisfies Instantaneo);
}

/** Guarda so a cauda de cada lista — a cota do localStorage e de ~5 MB. */
function podarMapa<T>(mapa: Record<string, T[]>, teto: number): Record<string, T[]> {
  const saida: Record<string, T[]> = {};
  for (const [id, lista] of Object.entries(mapa)) saida[id] = lista.slice(-teto);
  return saida;
}

/** Chamado no logout e ao detectar troca de conta. */
export function limpar(): void {
  try {
    const chaves: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const chave = localStorage.key(i);
      if (chave !== null && chave.startsWith(PREFIXO)) chaves.push(chave);
    }
    for (const chave of chaves) localStorage.removeItem(chave);
  } catch {
    /* storage negado — nao ha o que limpar */
  }
}
